// frontend/src/components/ClubeFormModal/ClubeFormModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Button, message, Row, Col, Select } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { updateClubeAssinatura, createClubeAssinatura, getPortadores, getProgramasDoPortador } from '../../services/milhasService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';

const { Option } = Select;

const ClubeFormModal = ({ visible, onClose, onSave, clube }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [form] = Form.useForm();
    const [portadores, setPortadores] = useState([]);
    const [programas, setProgramas] = useState([]);
    const [loadingPortadores, setLoadingPortadores] = useState(false);
    const [loadingProgramas, setLoadingProgramas] = useState(false);
    
    // Hook de mensagem interno para erros. Sucesso é via callback.
    const [messageApi, contextHolder] = message.useMessage();

    const isEditing = !!clube;

    useEffect(() => {
        const fetchPortadores = async () => {
            if (visible) {
                setLoadingPortadores(true);
                try {
                    const res = await getPortadores();
                    setPortadores(res.data);
                } catch (error) {
                    // 2. USO NO FETCH PORTADORES
                    messageApi.error(getErrorMessage(error, 'Erro ao buscar portadores.'));
                } finally {
                    setLoadingPortadores(false);
                }
            }
        };
        fetchPortadores();
    }, [visible, messageApi]);

    useEffect(() => {
        const setFormValues = async () => {
            if (visible && isEditing && clube && portadores.length > 0) {
                form.setFieldsValue({
                    ...clube,
                    data_adesao: clube.data_adesao ? dayjs(clube.data_adesao) : null,
                    data_termino_planejada: clube.data_termino_planejada
                        ? dayjs(clube.data_termino_planejada)
                        : null,
                    portador: clube.portador_id,
                });

                if (clube.portador_id) {
                    setLoadingProgramas(true);
                    try {
                        const res = await getProgramasDoPortador(clube.portador_id);
                        setProgramas(res.data);
                        form.setFieldsValue({ programa_portador: clube.programa_portador });
                    } catch (error) {
                        // 3. USO NO FETCH PROGRAMAS PARA EDIÇÃO
                        messageApi.error(getErrorMessage(error, 'Erro ao buscar programas do portador para edição.'));
                        setProgramas([]);
                    } finally {
                        setLoadingProgramas(false);
                    }
                }
            } else if (visible && !isEditing) {
                form.resetFields();
                setProgramas([]);
                // Com um único portador não há o que escolher: já deixa preenchido.
                if (portadores.length === 1) {
                    form.setFieldsValue({ portador: portadores[0].id });
                    await handlePortadorChange(portadores[0].id);
                }
            }
        };
        setFormValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isEditing, clube, form, portadores, messageApi]);

    const handlePortadorChange = async (portadorId) => {
        form.setFieldsValue({ programa_portador: null });
        if (!portadorId) {
            setProgramas([]);
            return;
        }
        setLoadingProgramas(true);
        try {
            const res = await getProgramasDoPortador(portadorId);
            setProgramas(res.data);
        } catch (error) {
            // 4. USO NO FETCH PROGRAMAS AO TROCAR PORTADOR
            messageApi.error(getErrorMessage(error, 'Erro ao buscar programas do portador.'));
            setProgramas([]);
        } finally {
            setLoadingProgramas(false);
        }
    };
    
    const handleSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                data_adesao: dayjs(values.data_adesao).format('YYYY-MM-DD'),
                data_termino_planejada: values.data_termino_planejada
                    ? dayjs(values.data_termino_planejada).format('YYYY-MM-DD')
                    : null,
            };
            delete payload.portador;

            let successMsg = '';
            if (isEditing) {
                await updateClubeAssinatura(clube.id, payload);
                successMsg = 'Clube atualizado com sucesso!';
            } else {
                await createClubeAssinatura(payload);
                successMsg = 'Clube adicionado com sucesso!';
            }
            
            // Passa para o pai
            onSave(closeOnSuccess, successMsg);

            if (!closeOnSuccess) {
                const currentPortador = values.portador;
                const currentPrograma = values.programa_portador;
                
                form.resetFields();
                // Mantém o contexto para facilitar cadastro em lote
                form.setFieldsValue({ 
                    portador: currentPortador,
                    programa_portador: currentPrograma
                });
            }
        } catch (error) {
            // 5. USO NO SUBMIT
            messageApi.error(getErrorMessage(error, 'Erro ao salvar o clube.'));
        }
    };

    return (
        <Modal
            title={isEditing ? 'Editar Clube' : 'Adicionar Novo Clube'}
            open={visible}
            onCancel={onClose}
            width={isMobile ? undefined : 700}
            {...getMobileFullscreenModalProps(isMobile)}
            footer={[
                <Button key="back" onClick={onClose} block={isMobile} size={isMobile ? 'large' : 'middle'}>Cancelar</Button>,
                !isEditing && <Button key="saveAndNew" onClick={() => handleSubmit(false)} block={isMobile} size={isMobile ? 'large' : 'middle'}>Salvar e Adicionar Outro</Button>,
                <Button key="submit" type="primary" onClick={() => handleSubmit(true)} block={isMobile} size={isMobile ? 'large' : 'middle'}>Salvar</Button>
            ]}
        >
            {contextHolder}
            <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'}>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="portador" label="Portador" rules={[{ required: true, message: 'Selecione um portador' }]}>
                            <Select placeholder="Selecione..." loading={loadingPortadores} onChange={handlePortadorChange} disabled={isEditing}>
                                {portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="programa_portador" label="Programa de Fidelidade" rules={[{ required: true, message: 'Selecione um programa' }]}>
                            <Select placeholder={form.getFieldValue('portador') ? "Selecione..." : "Selecione um portador primeiro"} loading={loadingProgramas} disabled={isEditing || !form.getFieldValue('portador')}>
                                {programas.map(p => <Option key={p.id} value={p.id}>{p.programa_nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="plano_clube" label="Nome do Plano" rules={[{ required: true, message: 'Este campo é obrigatório' }]}>
                    <Input />
                </Form.Item>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="pontos_mensais" label="Pontos Mensais" rules={[{ required: true, message: 'Este campo é obrigatório' }]}>
                            <InputNumber style={{ width: '100%' }} min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="valor_mensal" label="Valor Mensal (R$)" rules={[{ required: true, message: 'Este campo é obrigatório' }]}>
                            <InputNumber
                                prefix="R$ "
                                style={{ width: '100%' }}
                                precision={2}
                                step={0.01}
                                formatter={(value) => {
                                    if (value === null || value === undefined || value === '') return '';
                                    return new Intl.NumberFormat('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(value);
                                }}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    const num = parseInt(onlyNumbers, 10);
                                    return num / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="dia_credito_mensal" label="Dia do Crédito" rules={[{ required: true, message: 'Este campo é obrigatório' }]}>
                           <Select
                                showSearch
                                placeholder="Selecione o dia"
                                filterOption={(input, option) => 
                                    String(option.value).toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                    <Option key={day} value={day}>{String(day).padStart(2, '0')}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="data_adesao" label="Data de Adesão" rules={[{ required: true, message: 'Este campo é obrigatório' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name="data_termino_planejada"
                    label="Data de término planejada"
                    extra="Lembrete para revisar/cancelar o clube (ex.: downgrade Azul ou fim de promoção). Não encerra automaticamente."
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} allowClear placeholder="Opcional" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ClubeFormModal;